import React, { useState, useRef, useEffect } from 'react';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';

const ADMIN_EMAILS = ['shim.razilov@gmail.com', 'run.sente@gmail.com'];

const OrganizationSwitcher: React.FC = () => {
  const { user } = useAuth();
  const { organizations, currentOrganization, selectOrganization, isLoading } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Non-admins or single org: just show the name, no dropdown
  if (!isAdmin || organizations.length <= 1) {
    if (isLoading || !currentOrganization) return null;
    return (
      <div className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-slate-800/60 dark:to-slate-700/60 border border-gray-200 dark:border-slate-600/40 rounded-lg">
        <div className="w-5 h-5 rounded bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
          <span className="text-xs font-bold text-white">
            {currentOrganization.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[120px] sm:max-w-[200px] truncate">
          {currentOrganization.name}
        </span>
      </div>
    );
  }

  // Admin dropdown
  const filtered = search
    ? organizations.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : organizations;

  return (
    <div className="relative z-[150]" ref={dropdownRef}>
      <button
        onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
        disabled={isLoading}
        className="group flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border border-amber-200 dark:border-amber-600/40 rounded-lg backdrop-blur-sm hover:border-amber-300 dark:hover:border-amber-500/60 transition-all duration-300"
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
        ) : currentOrganization ? (
          <>
            <div className="w-5 h-5 rounded bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <span className="text-xs font-bold text-white">
                {currentOrganization.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[150px] truncate">
              {currentOrganization.name}
            </span>
          </>
        ) : (
          <span className="text-sm text-gray-500 dark:text-gray-400">Select org</span>
        )}
        <svg
          className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 w-80 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700/50 backdrop-blur-xl z-[200] overflow-hidden">
          {/* Search */}
          <div className="px-3 pt-3 pb-2">
            <input
              type="text"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 text-gray-700 dark:text-gray-200 placeholder-gray-400"
              autoFocus
            />
          </div>

          {/* Org list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">No matches</div>
            ) : (
              filtered.map(org => (
                <button
                  key={org.id}
                  onClick={() => {
                    selectOrganization(org.id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-2 text-left transition-all hover:bg-gray-100 dark:hover:bg-gray-700/50 ${
                    currentOrganization?.id === org.id ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                  }`}
                >
                  <div className="w-6 h-6 rounded bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-white">
                      {org.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate block">
                      {org.name}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {org.role}
                      {org.memberCount !== undefined && ` \u00B7 ${org.memberCount} member${org.memberCount !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                  {currentOrganization?.id === org.id && (
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700/50">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {organizations.length} organizations (admin view)
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSwitcher;
